import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Segmented,
  Statistic,
  Table,
  Progress,
  Tag,
  Space,
  Typography,
  Spin,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@services/api';
import { ExportButton } from '@components/ExportButton';
import { AutoRefreshToggle } from '@components/AutoRefreshToggle';
import {
  AnalyticsDateRangeFilter,
  type AnalyticsPeriod,
  type AnalyticsDateRange,
  type AnalyticsShownRange,
} from '@/components';
import { exportOptions } from '@/constants/exportOptions';
import { useAutoRefresh } from '@hooks/useAutoRefresh';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { analyticsWindow } from '@utils/analyticsWindow';
import { WicketDiagnostics } from '@/components/WicketDiagnostics';
import {
  bucketBreakdownRows,
  bucketDistributionRows,
  bucketRunRateRows,
  parseBucketValue,
  DIFFICULTY_BUCKET_OPTIONS,
  type DifficultyBucketSize,
} from '@/utils/difficultyBucket';
import type {
  GlobalAiAnalyticsResponse,
  AiBreakdownItem,
  AiDistributionItem,
  AiTeamCount,
} from '@/types';

const { Title } = Typography;

const breakdownColumns = [
  { title: 'Category', dataIndex: 'label', key: 'label' },
  { title: 'Matches', dataIndex: 'matches', key: 'matches', width: 80 },
  { title: 'W', dataIndex: 'wins', key: 'wins', width: 50 },
  { title: 'L', dataIndex: 'losses', key: 'losses', width: 50 },
  { title: 'T', dataIndex: 'ties', key: 'ties', width: 50 },
  {
    title: 'Win Rate',
    dataIndex: 'winRate',
    key: 'winRate',
    width: 150,
    render: (val: number) => (
      <Progress
        percent={val}
        size="small"
        format={(p) => `${p?.toFixed(1)}%`}
        strokeColor={val >= 60 ? '#52c41a' : val >= 40 ? '#faad14' : '#ff4d4f'}
      />
    ),
  },
];

const distColumns = [
  { title: 'Category', dataIndex: 'label', key: 'label' },
  { title: 'Count', dataIndex: 'count', key: 'count', width: 80 },
  {
    title: 'Percentage',
    dataIndex: 'percentage',
    key: 'percentage',
    width: 150,
    render: (val: number) => (
      <Progress percent={val} size="small" format={(p) => `${p?.toFixed(1)}%`} />
    ),
  },
];

const teamColumns = [
  { title: 'Team', dataIndex: 'team', key: 'team' },
  { title: 'Matches', dataIndex: 'count', key: 'count', width: 80 },
];

export const AiAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<GlobalAiAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('last_7_days');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30_000);
  const [difficultyBucket, setDifficultyBucket] = useState<DifficultyBucketSize>('individual');
  // The range and fetch time of the figures on screen, for the date label.
  const [shown, setShown] = useState<AnalyticsShownRange | null>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isLatest = beginRequest();
    setLoading(true);
    const startedAt = Date.now();
    const range: AnalyticsDateRange = {
      period: period as AnalyticsPeriod,
      customRange: customRange ?? undefined,
    };
    try {
      const params: Record<string, string> = {};
      if (period === 'custom' && customRange) {
        params.period = 'custom';
        params.startDate = customRange[0].format('YYYY-MM-DD');
        params.endDate = customRange[1].format('YYYY-MM-DD');
      } else {
        params.period = period;
      }
      const res = await adminApi.getGlobalAiAnalytics(params);
      if (!isLatest()) return;
      setData(res.data);
      setShown({ range, asOf: startedAt });
    } catch (err) {
      console.error('Failed to load AI analytics:', err);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [period, customRange, beginRequest]);

  // The wicket panels query exactly the labelled window, and only refetch
  // when it changes (an inline object made them refetch on every render).
  const wicketFilters = useMemo(() => {
    const labelled = shown && analyticsWindow(shown.range, shown.asOf);
    return labelled
      ? {
          period: 'custom',
          startDate: labelled.start.toISOString(),
          endDate: labelled.end.toISOString(),
        }
      : undefined;
  }, [shown]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useAutoRefresh(fetchData, autoRefresh, refreshInterval);

  const bucketedDifficultyDist = useMemo(
    () =>
      data
        ? bucketDistributionRows(
            data.difficultyDistribution,
            difficultyBucket,
            data.overview.totalMatches
          )
        : [],
    [data, difficultyBucket]
  );
  const bucketedWinRateByDiff = useMemo(
    () => (data ? bucketBreakdownRows(data.winRateByDifficulty, difficultyBucket) : []),
    [data, difficultyBucket]
  );
  const bucketedRunRate = useMemo(
    () => (data ? bucketRunRateRows(data.runRateByDifficulty, difficultyBucket) : []),
    [data, difficultyBucket]
  );
  // The same bucketing logic is safe to apply to AI Group + Division
  // panels — `bucketBreakdownRows` is a no-op on non-numeric `value`
  // (AI Group buckets are categorical strings, so they pass through
  // untouched; Division rows have integer values 1..21 which bucket
  // cleanly).
  const bucketedWinRateByAiGroup = useMemo(
    () => (data ? bucketBreakdownRows(data.winRateByAiGroup, difficultyBucket) : []),
    [data, difficultyBucket]
  );
  const bucketedWinRateByDivision = useMemo(
    () => (data ? bucketBreakdownRows(data.winRateByDivision, difficultyBucket) : []),
    [data, difficultyBucket]
  );
  // OS labels are categorical (`android` / `ios` / …) so the helper
  // passes through untouched. Build Version labels are numeric strings
  // and will re-aggregate cleanly under the page-level selector.
  const bucketedOsDistribution = useMemo(
    () =>
      data
        ? bucketDistributionRows(data.osDistribution, difficultyBucket, data.overview.totalMatches)
        : [],
    [data, difficultyBucket]
  );
  const bucketedBuildVersionDistribution = useMemo(
    () =>
      data
        ? bucketDistributionRows(
            data.buildVersionDistribution,
            difficultyBucket,
            data.overview.totalMatches
          )
        : [],
    [data, difficultyBucket]
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            AI Analytics
          </Title>
        </Col>
        <Col>
          <Space wrap>
            <Space size={6}>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>Bucket</span>
              <Segmented
                size="small"
                value={String(difficultyBucket)}
                onChange={(v) => setDifficultyBucket(parseBucketValue(String(v)))}
                options={[...DIFFICULTY_BUCKET_OPTIONS]}
              />
            </Space>
            <AnalyticsDateRangeFilter
              value={{
                period: period as AnalyticsPeriod,
                customRange: customRange ?? undefined,
              }}
              loading={loading}
              shown={shown}
              onApply={(next: AnalyticsDateRange) => {
                setPeriod(next.period);
                setCustomRange(next.customRange ?? null);
              }}
            />
            <ExportButton
              options={[
                exportOptions.globalAiAnalytics,
                exportOptions.matchBreakdown,
                exportOptions.suspectMatches,
                exportOptions.aiWicketDiagnostics,
              ]}
              buttonText="Export"
            />
            <AutoRefreshToggle
              enabled={autoRefresh}
              onEnabledChange={setAutoRefresh}
              intervalMs={refreshInterval}
              onIntervalChange={setRefreshInterval}
            />
          </Space>
        </Col>
      </Row>

      {loading && !data ? (
        <Card>
          <Spin />
        </Card>
      ) : !data ? (
        <Card>No AI analytics data available.</Card>
      ) : (
        <>
          {/* Overview Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Total Matches" value={data.overview.totalMatches} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Unique Players" value={data.overview.uniquePlayers} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Avg Matches/Player"
                  value={data.overview.avgMatchesPerPlayer}
                  precision={1}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Overall Win Rate"
                  value={data.overview.overallWinRate}
                  suffix="%"
                  precision={1}
                  valueStyle={{
                    color: data.overview.overallWinRate >= 50 ? '#3f8600' : '#cf1322',
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* PTR / OTR / Dot Balls */}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic title="Avg PTR" value={data.overview.avgPTR} precision={1} />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic title="Avg OTR" value={data.overview.avgOTR} precision={1} />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic
                  title="Avg User Dot Balls"
                  value={data.overview.avgUserDotBalls}
                  precision={1}
                />
              </Card>
            </Col>
          </Row>

          {/* Difficulty — bucket controlled by the page-header selector */}
          <Card title="Difficulty" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Typography.Text strong>Distribution</Typography.Text>
                <Table<AiDistributionItem>
                  dataSource={bucketedDifficultyDist}
                  columns={distColumns}
                  rowKey="label"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Col>
              <Col xs={24} lg={8}>
                <Typography.Text strong>Win Rate</Typography.Text>
                <Table<AiBreakdownItem>
                  dataSource={bucketedWinRateByDiff}
                  columns={breakdownColumns}
                  rowKey="label"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Col>
              <Col xs={24} lg={8}>
                <Typography.Text strong>Run Rate</Typography.Text>
                <Table
                  dataSource={bucketedRunRate}
                  columns={[
                    { title: 'Difficulty', dataIndex: 'difficulty', key: 'difficulty' },
                    {
                      title: 'User RR',
                      dataIndex: 'avgUserRunRate',
                      key: 'avgUserRunRate',
                      width: 90,
                      align: 'right',
                      render: (v: number) => v?.toFixed(2),
                    },
                    {
                      title: 'AI RR',
                      dataIndex: 'avgAiRunRate',
                      key: 'avgAiRunRate',
                      width: 90,
                      align: 'right',
                      render: (v: number) => v?.toFixed(2),
                    },
                    {
                      title: 'Matches',
                      dataIndex: 'matches',
                      key: 'matches',
                      width: 80,
                      align: 'right',
                    },
                  ]}
                  rowKey="difficulty"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Col>
            </Row>
          </Card>

          {/* AI Group & Division — bucket from the page-header selector */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Win Rate by AI Group" size="small">
                <Table<AiBreakdownItem>
                  dataSource={bucketedWinRateByAiGroup}
                  columns={breakdownColumns}
                  rowKey="label"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Win Rate by Division" size="small">
                <Table<AiBreakdownItem>
                  dataSource={bucketedWinRateByDivision}
                  columns={breakdownColumns}
                  rowKey="label"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Teams */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Most Used Player Teams" size="small">
                <Table<AiTeamCount>
                  dataSource={data.topUserTeams}
                  columns={teamColumns}
                  rowKey="team"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Most Used AI Teams" size="small">
                <Table<AiTeamCount>
                  dataSource={data.topAiTeams}
                  columns={teamColumns}
                  rowKey="team"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Batting Order */}
          <Card title="Batting Order Analysis" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={12}>
                <Statistic
                  title="Bat First"
                  value={data.battingOrder.batFirstMatches}
                  suffix={
                    <span style={{ fontSize: 14, color: '#888' }}>
                      ({data.battingOrder.batFirstWins}W - {data.battingOrder.batFirstWinRate}%)
                    </span>
                  }
                />
              </Col>
              <Col xs={12}>
                <Statistic
                  title="Bowl First"
                  value={data.battingOrder.bowlFirstMatches}
                  suffix={
                    <span style={{ fontSize: 14, color: '#888' }}>
                      ({data.battingOrder.bowlFirstWins}W - {data.battingOrder.bowlFirstWinRate}%)
                    </span>
                  }
                />
              </Col>
            </Row>
          </Card>

          {/* OS & Build Version */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="OS Distribution" size="small">
                <Table<AiDistributionItem>
                  dataSource={bucketedOsDistribution}
                  columns={[
                    { title: 'OS', dataIndex: 'label', key: 'label' },
                    { title: 'Count', dataIndex: 'count', key: 'count', width: 80 },
                    {
                      title: '%',
                      dataIndex: 'percentage',
                      key: 'percentage',
                      width: 60,
                      render: (v: number) => `${v}%`,
                    },
                  ]}
                  rowKey="label"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Build Version Distribution" size="small">
                <Table<AiDistributionItem>
                  dataSource={bucketedBuildVersionDistribution}
                  columns={[
                    { title: 'Build', dataIndex: 'label', key: 'label' },
                    { title: 'Count', dataIndex: 'count', key: 'count', width: 80 },
                    {
                      title: '%',
                      dataIndex: 'percentage',
                      key: 'percentage',
                      width: 60,
                      render: (v: number) => `${v}%`,
                    },
                  ]}
                  rowKey="label"
                  pagination={false}
                  size="small"
                  scroll={{ y: 320 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Auto-play */}
          <Card title="Auto-Play Usage" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={12}>
                <Statistic
                  title="Auto-Play Batting"
                  value={data.autoPlay.autoPlayBatPercent}
                  suffix="%"
                  precision={1}
                />
                <Tag style={{ marginTop: 4 }}>
                  {data.autoPlay.autoPlayBatMatches} / {data.autoPlay.totalMatches} matches
                </Tag>
              </Col>
              <Col xs={12}>
                <Statistic
                  title="Auto-Play Bowling"
                  value={data.autoPlay.autoPlayBowlPercent}
                  suffix="%"
                  precision={1}
                />
                <Tag style={{ marginTop: 4 }}>
                  {data.autoPlay.autoPlayBowlMatches} / {data.autoPlay.totalMatches} matches
                </Tag>
              </Col>
            </Row>
          </Card>

          {/* AI Batting Diagnostics — wicket-level drill-down */}
          <WicketDiagnostics filters={wicketFilters ?? { period }} />
        </>
      )}
    </Space>
  );
};
